import { digitsIn, MAX_METHOD, NAME_THE_CARD } from "../../src/booking.ts";
import type { BookingEdit, Item, When } from "../../src/web/types.ts";
import { zonedToUtc } from "../../src/zoned.ts";

// The booking form's fields (BookingForm.tsx; DESIGN.md, Views, "Record a booking where it's made"), as typed:
// read from the item, checked, and made into what the form sends, only what was changed. Plain, so the unit
// tests read it too (test/booking-draft.test.ts).

// What it's booked as, for its when: a stay's check-in and check-out, a journey's departure and arrival (each
// end in its own time zone), a place's date and time.
export type BookingKind = "stay" | "move" | "place";
export const bookingKind = (item: Pick<Item, "type">): BookingKind =>
  item.type === "stay" ? "stay" : item.type === "move" ? "move" : "place";

// A payment as typed: who, how much in what, and how it was paid; or, charged later (features/bookings-and-costs.md,
// "Charged later"), whose card, how much, which card, and the day it's charged (`on`).
export interface Line {
  key: string;
  by: string;
  amount: string;
  currency: string;
  method: string;
  later: boolean;
  on: string;
}

export interface Draft {
  // When: a stay's check-in (start) and check-out (end), one time zone (the start's); a journey's departure
  // and arrival, each in its own; a place's date, its time from (start) and until (end, the same day).
  startDate: string;
  startTime: string;
  startTz: string;
  endDate: string;
  endTime: string;
  endTz: string;
  // The price.
  amount: string;
  currency: string;
  per: "group" | "person";
  lines: Line[];
  provider: string;
  ref: string;
  url: string;
  bookedBy: string;
  // Free cancellation until: a date, and a time if it says one.
  freeDate: string;
  freeTime: string;
}

// The form's parts, each sent whole when anything in it changed.
const GROUPS = {
  when: ["startDate", "startTime", "startTz", "endDate", "endTime", "endTz"],
  price: ["amount", "currency", "per"],
  paid: ["lines"],
  provider: ["provider"],
  ref: ["ref"],
  url: ["url"],
  bookedBy: ["bookedBy"],
  free: ["freeDate", "freeTime"],
} as const satisfies Record<string, readonly (keyof Draft)[]>;
export type Group = keyof typeof GROUPS;
export const FORM_GROUPS = Object.keys(GROUPS) as Group[];

const isDate = (d: string | undefined): d is string => d !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(d);

// The policy, when the form can change it: none yet, non-refundable, or one full-refund step. Otherwise (steps
// with part of the money back) only its words show, and the AI changes it.
export function freeStep(item: Pick<Item, "booking">): { until: string | null } | null {
  const policy = item.booking?.cancellation;
  if (!policy || policy.steps.length === 0) return { until: null };
  const [step] = policy.steps;
  return policy.steps.length === 1 && step?.refund === "full" ? { until: step.until } : null;
}

let lineKeys = 0;
export const newLine = (fields: Omit<Line, "key" | "later" | "on"> & Partial<Pick<Line, "later" | "on">>): Line => ({
  key: `line-${++lineKeys}`,
  later: false,
  on: "",
  ...fields,
});

// The form as the item is now. `zones`: each end's time zone where the item has none (its place's, else the
// trip's); `currency`: the price's where it has none; `me`: who booked it where nobody's named (none: as it is).
// `people`: everyone in the trip, a payment by a member id read as the person.
export function draftOf(
  item: Item,
  zones: { start: string; end: string },
  defaults: { currency: string; me: string | null; people: { id: string; member?: string | undefined }[] },
): Draft {
  const w: When | undefined = item.when;
  const when = {
    startDate: "",
    startTime: "",
    startTz: zones.start,
    endDate: "",
    endTime: "",
    endTz: zones.end,
  };
  if (w?.precision === "day" && isDate(w.day)) when.startDate = w.day;
  if (w?.precision === "days") {
    if (isDate(w.start)) when.startDate = w.start;
    if (isDate(w.end)) when.endDate = w.end;
  }
  if (w?.precision === "part" && isDate(w.day)) when.startDate = w.day;
  if (w?.precision === "exact" || w?.precision === "window") {
    when.startDate = w.start.slice(0, 10);
    when.startTime = w.start.slice(11, 16);
    when.startTz = w.tz;
    when.endTz = (w.precision === "exact" && w.end_tz) || w.tz;
    if (w.end) {
      when.endDate = w.end.slice(0, 10);
      when.endTime = w.end.slice(11, 16);
    }
  }
  const person = (ref: string) => defaults.people.find((p) => p.member === ref || p.id === ref)?.id ?? ref;
  const free = freeStep(item)?.until ?? "";
  return {
    ...when,
    amount: item.cost ? String(item.cost.amount) : "",
    currency: item.cost?.currency ?? defaults.currency,
    per: item.cost?.per ?? "group",
    // What's paid, then what's charged later.
    lines: [
      ...(item.paid ?? []).map((p) =>
        newLine({ by: person(p.by), amount: String(p.amount), currency: p.currency, method: p.method ?? "" }),
      ),
      ...(item.charges ?? []).map((c) =>
        newLine({
          by: person(c.by),
          amount: String(c.amount),
          currency: c.currency,
          method: c.method ?? "",
          later: true,
          on: c.on,
        }),
      ),
    ],
    provider: item.booking?.provider ?? "",
    ref: item.booking?.ref ?? "",
    url: item.booking?.url ?? "",
    bookedBy: item.booking?.booked_by ? person(item.booking.booked_by) : (defaults.me ?? ""),
    freeDate: free.slice(0, 10),
    freeTime: free.slice(11, 16),
  };
}

const groupValue = (d: Draft, g: Group) =>
  JSON.stringify(
    GROUPS[g].map((k) =>
      k === "lines" ? d.lines.map(({ key: _, ...line }) => line) : d[k as Exclude<keyof Draft, "lines">],
    ),
  );
export const changed = (a: Draft, b: Draft, g: Group) => groupValue(a, g) !== groupValue(b, g);

// After someone else's change: each part I didn't touch as it is now, each part only I changed as I left it, and
// where we both changed one, theirs (`theirs` lists those), so nothing of theirs is written over.
export function rebase(mine: Draft, was: Draft, now: Draft): { draft: Draft; theirs: Group[] } {
  const draft = { ...mine };
  const theirs: Group[] = [];
  for (const g of FORM_GROUPS) {
    if (!changed(mine, was, g) || !changed(now, was, g)) {
      if (!changed(mine, was, g)) Object.assign(draft, pick(now, g));
      continue;
    }
    Object.assign(draft, pick(now, g));
    theirs.push(g);
  }
  return { draft, theirs };
}
const pick = (d: Draft, g: Group): Partial<Draft> => Object.fromEntries(GROUPS[g].map((k) => [k, d[k]]));

export interface Problem {
  // The field it's about: a Draft key, or a payment's ("<line key>.amount").
  field: string;
  message: string;
}

// An amount as typed: "1,074.26", "79 200", "609.93". Null when it isn't one.
export function amountOf(text: string): number | null {
  const plain = text.trim().replace(/[,\s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(plain)) return null;
  const n = Number(plain);
  return Number.isFinite(n) ? n : null;
}

// A link as typed, made whole: "expedia.com/trips" is https://expedia.com/trips. Null when it isn't one.
export function linkOf(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(t) ? t : `https://${t}`);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname.includes(".") ? url.href : null;
  } catch {
    return null;
  }
}

const nextDay = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// The when the form says, or what's wrong with it.
export function whenOf(kind: BookingKind, d: Draft): { when: When | null; problems: Problem[] } {
  const problems: Problem[] = [];
  const say = (field: keyof Draft, message: string) => problems.push({ field, message });
  const nothing = { when: null, problems };
  const start = d.startTime ? `${d.startDate}T${d.startTime}` : d.startDate;
  if (kind === "stay") {
    if (!d.startDate && !d.endDate && !d.startTime && !d.endTime) return nothing;
    if (!d.startDate) say("startDate", "Add the check-in date.");
    if (!d.endDate && (d.endTime || d.startTime)) say("endDate", "Add the check-out date.");
    if (d.startDate && d.endDate && d.endDate < d.startDate) say("endDate", "Check-out comes after check-in.");
    if (Boolean(d.startTime) !== Boolean(d.endTime))
      say(d.startTime ? "endTime" : "startTime", "Give both times, or neither.");
    if (problems.length) return nothing;
    if (d.startTime)
      return {
        when: { precision: "exact", start, end: `${d.endDate}T${d.endTime}`, tz: d.startTz },
        problems,
      };
    return {
      when: d.endDate
        ? { precision: "days", start: d.startDate, end: d.endDate }
        : { precision: "day", day: d.startDate },
      problems,
    };
  }
  if (kind === "move") {
    if (!d.startDate && !d.endDate && !d.startTime && !d.endTime) return nothing;
    if (!d.startDate) say("startDate", "Add the departure date.");
    if (d.endTime && !d.startTime) say("startTime", "Add the departure time too.");
    if (d.startDate && d.endDate && d.endDate < d.startDate) say("endDate", "Arrival comes after departure.");
    if (problems.length) return nothing;
    if (!d.startTime)
      return {
        when:
          d.endDate && d.endDate !== d.startDate
            ? { precision: "days", start: d.startDate, end: d.endDate }
            : { precision: "day", day: d.startDate },
        problems,
      };
    const end = d.endTime ? `${d.endDate || d.startDate}T${d.endTime}` : null;
    if (end && zonedToUtc(end, d.endTz) <= zonedToUtc(start, d.startTz)) {
      say("endTime", "Arrival comes after departure, in their time zones.");
      return nothing;
    }
    return {
      when: {
        precision: "exact",
        start,
        tz: d.startTz,
        ...(end ? { end, ...(d.endTz !== d.startTz ? { end_tz: d.endTz } : {}) } : {}),
      },
      problems,
    };
  }
  if (!d.startDate && !d.startTime && !d.endTime) return nothing;
  if (!d.startDate) say("startDate", "Add the date.");
  if (d.endTime && !d.startTime) say("startTime", "Add the time it starts too.");
  if (problems.length) return nothing;
  if (!d.startTime) return { when: { precision: "day", day: d.startDate }, problems };
  // Until a time earlier than it starts: past midnight.
  const endDay = d.endTime && d.endTime <= d.startTime ? nextDay(d.startDate) : d.startDate;
  return {
    when: {
      precision: "exact",
      start,
      ...(d.endTime ? { end: `${endDay}T${d.endTime}` } : {}),
      tz: d.startTz,
    },
    problems,
  };
}

// A payment as typed, made into what's sent (the booking form's lines, Add a payment), or what's wrong with it; one
// charged later, with its day (`on`, kept in `payment` for the form to send apart). `kept`: the methods the thing has
// already, which stay as they are even with digits in them (src/booking.ts, digitsAdded: the card by name, never its
// digits, for what's new or changed).
export function paymentOf(
  line: Line,
  kept: ReadonlySet<string> = new Set(),
): {
  payment: { by: string; amount: number; currency: string; method?: string; on?: string } | null;
  problems: Problem[];
} {
  const problems: Problem[] = [];
  const amount = amountOf(line.amount);
  const method = line.method.trim();
  if (!line.by) problems.push({ field: `${line.key}.by`, message: line.later ? "Whose card?" : "Who paid?" });
  if (line.later && !isDate(line.on))
    problems.push({ field: `${line.key}.on`, message: "The day the card is charged." });
  if (amount === null || amount === 0)
    problems.push({ field: `${line.key}.amount`, message: "How much, like 609.93." });
  if (method.length > MAX_METHOD)
    problems.push({ field: `${line.key}.method`, message: `A few words: ${MAX_METHOD} characters at most.` });
  else if (digitsIn(method) && !kept.has(method))
    problems.push({ field: `${line.key}.method`, message: NAME_THE_CARD });
  return {
    payment: amount
      ? {
          by: line.by,
          amount,
          currency: line.currency,
          ...(method ? { method } : {}),
          ...(line.later && isDate(line.on) ? { on: line.on } : {}),
        }
      : null,
    problems,
  };
}

// What the form sends: the version it read, and each part it changed (from `base`, the form as the item was),
// or what's wrong. `estimate`: the item's price is an estimate so far; saved with an amount, the form's price is
// the price as booked, changed or not.
export function editOf(
  kind: BookingKind,
  draft: Draft,
  base: Draft,
  version: number,
  estimate = false,
): { edit: BookingEdit; problems: [] } | { edit: null; problems: Problem[] } {
  const problems: Problem[] = [];
  const edit: BookingEdit = { version };
  if (changed(draft, base, "when")) {
    const { when, problems: wrong } = whenOf(kind, draft);
    problems.push(...wrong);
    edit.when = when;
  }
  if (changed(draft, base, "price") || (estimate && draft.amount.trim())) {
    if (!draft.amount.trim()) edit.cost = null;
    else {
      const amount = amountOf(draft.amount);
      if (amount === null) problems.push({ field: "amount", message: "An amount, like 609.93." });
      else edit.cost = { amount, currency: draft.currency, per: draft.per };
    }
  }
  if (changed(draft, base, "paid")) {
    // What's paid, and what's charged later when there is or was any.
    const paid: NonNullable<BookingEdit["paid"]> = [];
    const charges: NonNullable<BookingEdit["charges"]> = [];
    const kept = new Set(base.lines.map((l) => l.method.trim()).filter(Boolean));
    for (const line of draft.lines) {
      const made = paymentOf(line, kept);
      problems.push(...made.problems);
      if (!made.payment) continue;
      const { on, ...payment } = made.payment;
      if (line.later && on) charges.push({ ...payment, on });
      else paid.push(payment);
    }
    edit.paid = paid;
    if ([...draft.lines, ...base.lines].some((l) => l.later)) edit.charges = charges;
  }
  for (const [group, key] of [
    ["provider", "provider"],
    ["ref", "ref"],
  ] as const)
    if (changed(draft, base, group)) edit[key] = draft[key].trim() || null;
  if (changed(draft, base, "url")) {
    const url = linkOf(draft.url);
    if (draft.url.trim() && !url) problems.push({ field: "url", message: "A link, like https://expedia.com/trips." });
    edit.url = url;
  }
  if (changed(draft, base, "bookedBy")) edit.booked_by = draft.bookedBy || null;
  if (changed(draft, base, "free")) {
    if (draft.freeTime && !draft.freeDate) problems.push({ field: "freeDate", message: "Add the date too." });
    edit.free_until = draft.freeDate ? (draft.freeTime ? `${draft.freeDate}T${draft.freeTime}` : draft.freeDate) : null;
  }
  return problems.length ? { edit: null, problems } : { edit, problems: [] };
}

// The trip's own time zone, for something with no time and no place: the one its times are most often in, else
// this device's.
export function tripZone(items: Pick<Item, "when">[], device: string | null): string {
  const counts = new Map<string, number>();
  for (const i of items) {
    const w = i.when;
    if (w && (w.precision === "exact" || w.precision === "window")) counts.set(w.tz, (counts.get(w.tz) ?? 0) + 1);
  }
  const [top] = [...counts].sort((a, b) => b[1] - a[1]);
  return top?.[0] ?? device ?? "UTC";
}
